import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Chip,
  Alert,
  Divider,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Header from "../../../../../components/Header";
import { tokens } from "../../../../../theme";
import { AssetFinanceUrl } from "../../../../../config";
import {
  executeAnyMutation,
  fetchJsonDynamic,
} from "../../../../../data/DynamicServiceNew/jsondynamicservice";

const base_url = AssetFinanceUrl.uri;

// ─────────────────────────────────────────────────────────────
// FILE STORAGE CONFIG
// ─────────────────────────────────────────────────────────────
const FILE_GATEWAY_URL = "https://api.sheriapro.sababisha.com";
const FILE_STORAGE_API_KEY =
  "E3VF4C6KFV43JH6GKCDFGJP45V36JHGV3H4C6F3GJC63HG405GH6V345GHHJ4623FJJL3HCVMO1P23PZ07W8";

const uploadFileToStorage = async (
  file,
  project = "loanmanagement",
  folder = "collateral",
) => {
  const form = new FormData();
  form.append("file", file);
  form.append("project", project);
  form.append("folder", folder);

  const response = await fetch(
    `${FILE_GATEWAY_URL}/filestorageservice/upload`,
    {
      method: "POST",
      headers: { "X-Api-Key": FILE_STORAGE_API_KEY },
      body: form,
    },
  );

  if (!response.ok) throw new Error(`Upload failed (${response.status})`);
  return await response.json();
};

const deleteFileFromStorage = async (objectName) => {
  await fetch(`${FILE_GATEWAY_URL}/filestorageservice/delete`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      "X-Api-Key": FILE_STORAGE_API_KEY,
    },
    body: JSON.stringify({ ObjectName: objectName }),
  });
};

// ─────────────────────────────────────────────────────────────

const TIER_META = {
  REQUIRED: { label: "Mandatory", color: "error" },
  CONDITIONAL: { label: "If Applicable", color: "warning" },
  SUPPORTING: { label: "Optional", color: "default" },
};
const OWNERSHIP_TYPES = [
  { value: "SINGLE", label: "Single Owner (this customer)" },
  { value: "CO_OWNED", label: "Co-Owned (customer + another person)" },
  { value: "COMPANY_OWNED", label: "Company Owned" },
];

const parseResults = (response) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.results)) return response.results;
  if (typeof response?.results === "string") {
    try {
      const parsed = JSON.parse(response.results);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  if (typeof response === "string") {
    try {
      const parsed = JSON.parse(response);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const parseMetadata = (metadataString) => {
  if (!metadataString) return {};
  try {
    let res =
      typeof metadataString === "string"
        ? JSON.parse(metadataString)
        : metadataString;
    if (typeof res === "string") res = JSON.parse(res);
    return res || {};
  } catch {
    return {};
  }
};

const matchBodyTypeToSubType = (bodyType, subTypeName) => {
  if (!subTypeName) return true;
  if (!bodyType) return true;
  const bt = String(bodyType).toLowerCase().trim();
  const st = String(subTypeName).toLowerCase().trim();

  if (bt === st || bt.includes(st) || st.includes(bt)) return true;

  // SUV
  if (st.includes("suv") || st.includes("sport utility")) {
    return bt === "suv" || bt.includes("suv") || bt.includes("station wagon");
  }

  // Saloon car / Sedan / Hatchback / Coupe
  if (st.includes("saloon") || st.includes("sedan") || st.includes("hatchback")) {
    return (
      bt.includes("sedan") ||
      bt.includes("saloon") ||
      bt.includes("hatchback") ||
      bt.includes("coupe") ||
      bt.includes("station wagon")
    );
  }

  // Pickup
  if (st.includes("pickup") || st.includes("pick-up") || st.includes("pick up")) {
    return bt.includes("pickup") || bt.includes("pick-up");
  }

  // Van / Minibus / Matatu
  if (st.includes("van") || st.includes("minibus") || st.includes("matatu")) {
    return bt.includes("van") || bt.includes("minibus");
  }

  // Lorry / Truck / Heavy Commercial / FH
  if (
    st.includes("lorry") ||
    st.includes("truck") ||
    st.includes("commercial") ||
    st.includes("fh") ||
    st.includes("trailer")
  ) {
    return bt.includes("lorry") || bt.includes("truck");
  }

  // Bus
  if (st.includes("bus")) {
    return bt.includes("bus");
  }

  return false;
};

const VEHICLE_ASSET_TYPE_CODE = "VEHICLE";
const VEHICLE_REQUIRED_FIELDS = [
  "regNumber",
  "make",
  "vehicleModel",
  "chassisNumber",
  "engineNumber",
  "yearOfManufacture",
  "colour",
];

const validationSchema = yup.object().shape({
  assetTypeId: yup
    .number()
    .typeError("Asset type is required")
    .required("Asset type is required"),
  subTypeId: yup
    .number()
    .typeError("Asset sub-type is required")
    .required("Asset sub-type is required"),
  indicativeValue: yup
    .number()
    .typeError("Enter a valid amount")
    .positive("Value must be greater than zero")
    .required("Indicative value is required"),
  ltvPct: yup
    .number()
    .typeError("Enter a valid percentage")
    .min(0, "LTV cannot be negative")
    .max(100, "LTV percentage cannot exceed 100%")
    .nullable(),
  description: yup.string().nullable(),
  isThirdPartyAcquisition: yup.boolean(),
  requireLien: yup.boolean(),
  isVehicleAsset: yup.boolean(),
  regNumber: yup.string().when("isVehicleAsset", {
    is: true,
    then: (s) => s.required("Number plate is required"),
    otherwise: (s) => s.nullable(),
  }),
  make: yup.string().when("isVehicleAsset", {
    is: true,
    then: (s) => s.required("Make is required"),
    otherwise: (s) => s.nullable(),
  }),
  vehicleModel: yup.string().when("isVehicleAsset", {
    is: true,
    then: (s) => s.required("Model is required"),
    otherwise: (s) => s.nullable(),
  }),
  chassisNumber: yup.string().when("isVehicleAsset", {
    is: true,
    then: (s) => s.required("Chassis / VIN number is required"),
    otherwise: (s) => s.nullable(),
  }),
  engineNumber: yup.string().when("isVehicleAsset", {
    is: true,
    then: (s) => s.required("Engine number is required"),
    otherwise: (s) => s.nullable(),
  }),
  yearOfManufacture: yup.mixed().when("isVehicleAsset", {
    is: true,
    then: (s) =>
      s
        .required("Year of manufacture is required")
        .test(
          "not-empty",
          "Year of manufacture is required",
          (v) => v !== "" && v != null,
        ),
    otherwise: (s) => s.nullable(),
  }),
  colour: yup.string().when("isVehicleAsset", {
    is: true,
    then: (s) => s.required("Colour is required"),
    otherwise: (s) => s.nullable(),
  }),
});

const CollateralAssetForm = (props) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const swal = (options) =>
    Swal.fire({
      ...options,
      didOpen: () => {
        const el = document.querySelector(".swal2-container");
        if (el) el.style.zIndex = "99999";
        options?.didOpen?.();
      },
    });

  const [assetTypes, setAssetTypes] = useState([]);
  const [subTypes, setSubTypes] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [loadingDependents, setLoadingDependents] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [carModels, setCarModels] = useState([]);
  const [loadingCarModels, setLoadingCarModels] = useState(false);

  const [stagedFiles, setStagedFiles] = useState({});
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  const [fileErrors, setFileErrors] = useState({});
  const [docActionErrors, setDocActionErrors] = useState({});
  const [deletingDocId, setDeletingDocId] = useState(null);

  // Only the company-owner record's Id needs tracking here now — co-owner and
  // director records are managed entirely in their own dedicated tables/forms.
  const [existingCompanyOwnerId, setExistingCompanyOwnerId] = useState(null);

  const existingMetadata = parseMetadata(
    props.data?.Metadata || props.data?.metadata,
  );
  const rawAssetTypeId =
    props.data?.AssetTypeId ||
    props.data?.AssetType ||
    props.data?.assetTypeId ||
    props.data?.assetType;
  const existingAssetTypeId =
    rawAssetTypeId !== "" && rawAssetTypeId != null
      ? Number(rawAssetTypeId)
      : "";

  const rawSubTypeId =
    props.data?.SubTypeId ||
    props.data?.SubType ||
    props.data?.subTypeId ||
    props.data?.subType;
  const existingSubTypeId =
    rawSubTypeId !== "" && rawSubTypeId != null ? Number(rawSubTypeId) : "";
  const [companyOwnerDetails, setCompanyOwnerDetails] = useState({
    companyName: "",
    companyRegNumber: "",
    companyKraPin: "",
  });

  const isVehicleType = assetTypes.some(
    (t) =>
      Number(t.Id) === Number(existingAssetTypeId) &&
      t.Code === VEHICLE_ASSET_TYPE_CODE,
  );

  const setFieldValueRef = React.useRef(() => {});
  const initialValues = {
    assetTypeId: existingAssetTypeId,
    subTypeId: existingSubTypeId,
    indicativeValue:
      props.data?.IndicativeValue ?? props.data?.indicativeValue ?? "",
    ltvPct:
      props.data?.LtvPct ?? props.data?.ltvPct ?? existingMetadata.ltvPct ?? "",
    description: existingMetadata.description || "",
    isThirdPartyAcquisition: !!existingMetadata.isThirdPartyAcquisition,
    isVehicleAsset: isVehicleType,
    regNumber: existingMetadata.regNumber || "",
    make: existingMetadata.make || "",
    vehicleModel: existingMetadata.vehicleModel || "",
    chassisNumber: existingMetadata.chassisNumber || "",
    engineNumber: existingMetadata.engineNumber || "",
    yearOfManufacture: existingMetadata.yearOfManufacture || "",
    colour: existingMetadata.colour || "",
    requireLien: !!(props.data?.RequireLien ?? props.data?.requireLien),
    ownershipType: props.data?.OwnershipType || "SINGLE",
    // companyName: existingMetadata.companyName || "",
    // companyRegNumber: existingMetadata.companyRegNumber || "",
    // companyKraPin: existingMetadata.companyKraPin || "",
    companyName:
      companyOwnerDetails.companyName || existingMetadata.companyName || "",
    companyRegNumber:
      companyOwnerDetails.companyRegNumber ||
      existingMetadata.companyRegNumber ||
      "",
    companyKraPin:
      companyOwnerDetails.companyKraPin || existingMetadata.companyKraPin || "",
  };

  const userChangedRef = React.useRef(false);
  const formikRef = React.useRef(null);

  useEffect(() => {
    userChangedRef.current = false;
  }, [props.data]);

  useEffect(() => {
    (async () => {
      try {
        const response = await fetchJsonDynamic(
          "allAssetTypes",
          null,
          null,
          base_url,
        );
        const results = parseResults(response);
        const filtered = results
          .filter((t) => t.IsActive !== false && t.IsActive !== "false")
          .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0));
        const finalTypes = filtered.length > 0 ? filtered : results;
        setAssetTypes(finalTypes);

        if (props.isEditing) {
          const existingAssetTypeId =
            props.data?.AssetTypeId || props.data?.AssetType;
          if (existingAssetTypeId !== "" && existingAssetTypeId != null) {
            const matched = finalTypes.find(
              (t) => Number(t.Id) === Number(existingAssetTypeId),
            );
            if (matched?.Code === VEHICLE_ASSET_TYPE_CODE) {
              formikRef.current?.setFieldValue("isVehicleAsset", true);
            }
          }
        }
      } catch (err) {
        console.error("Failed to load asset types:", err);
      } finally {
        setLoadingTypes(false);
      }
    })();
  }, []);

  const [carMakesList, setCarMakesList] = useState([]);
  useEffect(() => {
    (async () => {
      setLoadingCarModels(true);
      try {
        const modelsRes = await fetchJsonDynamic(
          "allCarModels",
          null,
          null,
          base_url,
        );
        const modelsList = parseResults(modelsRes).filter(
          (m) => m.IsActive !== false,
        );
        setCarModels(modelsList);

        try {
          const makesRes = await fetchJsonDynamic(
            "allCarMakes",
            null,
            null,
            base_url,
          );
          if (makesRes && !makesRes.error) {
            const makesList = parseResults(makesRes).filter(
              (m) => m.IsActive !== false,
            );
            setCarMakesList(makesList);
          }
        } catch {
          // allCarMakes not yet deployed
        }
      } catch (err) {
        console.error("Failed to load car models/makes:", err);
      } finally {
        setLoadingCarModels(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (carModels.length > 0 && formikRef.current) {
      const { make, vehicleModel, ltvPct } = formikRef.current.values || {};
      if (vehicleModel && (ltvPct === "" || ltvPct == null)) {
        const selectedMakeObj = carMakesList.find(
          (m) => (m.MakeName || m.Name || m.Make) === make,
        );
        const selectedMakeId = selectedMakeObj?.Id || selectedMakeObj?.id;
        const matchedModel = carModels.find((m) => {
          const nameMatch = m.ModelName === vehicleModel;
          if (!nameMatch) return false;
          if (selectedMakeId && m.MakeId) {
            return Number(m.MakeId) === Number(selectedMakeId);
          }
          return (
            m.Make === make ||
            (m.Make && make && m.Make.toLowerCase() === make.toLowerCase())
          );
        });
        if (matchedModel) {
          const modelLtv = matchedModel.LtvPct ?? matchedModel.ltvPct;
          if (modelLtv !== undefined && modelLtv !== null) {
            setFieldValueRef.current("ltvPct", modelLtv);
          }
        }
      }
    }
  }, [carModels, carMakesList]);

  const loadDependents = async (assetTypeId, ownershipType = "SINGLE") => {
    if (!assetTypeId) {
      setSubTypes([]);
      setRequirements([]);
      return;
    }
    setLoadingDependents(true);
    try {
      const requirementParams =
        ownershipType === "COMPANY_OWNED"
          ? { assetType: Number(assetTypeId), ownershipType }
          : { assetType: Number(assetTypeId) };

      const [subTypeResponse, requirementResponse] = await Promise.all([
        fetchJsonDynamic(
          "assetSubTypesByAssetTypeId",
          "subType",
          { assetTypeId: Number(assetTypeId), code: null },
          base_url,
        ),
        fetchJsonDynamic(
          "assetDocumentRequirements",
          "requirement",
          requirementParams,
          base_url,
        ),
      ]);
      setSubTypes(
        parseResults(subTypeResponse)
          .filter((s) => s.IsActive !== false)
          .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0)),
      );
      setRequirements(
        parseResults(requirementResponse)
          .filter((r) => r.IsActive == true)
          .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0)),
      );
    } catch (err) {
      console.error("Failed to load asset dependents:", err);
      setSubTypes([]);
      setRequirements([]);
    } finally {
      setLoadingDependents(false);
    }
  };

  useEffect(() => {
    const assetTypeId = props.data?.AssetTypeId || props.data?.AssetType;
    if (assetTypeId) loadDependents(assetTypeId);
  }, []);

  useEffect(() => {
    if (!props.isEditing || !props.data?.Id) return;
    (async () => {
      setLoadingDocuments(true);
      try {
        const res = await fetchJsonDynamic(
          "collateralAssetDocumentByColumnValues",
          "collateralDocument",
          { collateralAssetId: Number(props.data.Id) },
          base_url,
        );
        setExistingDocuments(parseResults(res));
      } catch {
        setExistingDocuments([]);
      }
      setLoadingDocuments(false);
    })();
  }, [props.isEditing, props.data?.Id]);

  const [loadingOwnership, setLoadingOwnership] = useState(false);

  // Only company details (name / reg number / KRA pin) are hydrated here now.
  // Co-owner details and directors are managed in their own tables.
  // 1. New state — holds the fetched company-owner record

  // 2. In the fetch effect — replace the imperative setFieldValue calls
  useEffect(() => {
    if (!props.isEditing || !props.data?.Id) return;
    const ownershipType = props.data?.OwnershipType || "SINGLE";
    if (ownershipType !== "COMPANY_OWNED") return;

    (async () => {
      setLoadingOwnership(true);
      try {
        const ownerRes = await fetchJsonDynamic(
          "collateralCompanyOwnerByColumnValues",
          "companyOwner",
          { collateralAssetId: Number(props.data.Id) },
          base_url,
        );
        const [companyOwner] = parseResults(ownerRes);
        if (companyOwner) {
          setExistingCompanyOwnerId(companyOwner.Id ?? null);
          setCompanyOwnerDetails({
            companyName: companyOwner.CompanyName || "",
            companyRegNumber: companyOwner.RegistrationNumber || "",
            companyKraPin: companyOwner.KraPin || "",
          });
        }
      } catch {
        // non-fatal — form just stays blank for company fields
      }
      setLoadingOwnership(false);
    })();
  }, [props.isEditing, props.data?.Id]);

  // 3. In initialValues — source from the fetched state, not just metadata

  const getExistingDocument = (requirementId) =>
    existingDocuments.find((d) => d.RequirementId === requirementId);

  const isDuplicateDocumentName = (fileName, requirementId) => {
    const normalized = fileName.trim().toLowerCase();

    const duplicateInStaged = Object.entries(stagedFiles).some(
      ([reqId, staged]) =>
        Number(reqId) !== requirementId &&
        staged.file?.name?.trim().toLowerCase() === normalized,
    );
    if (duplicateInStaged) return true;

    return existingDocuments.some(
      (doc) =>
        doc.RequirementId !== requirementId &&
        (doc.FileName || "").trim().toLowerCase() === normalized,
    );
  };

  const handleDocumentSelect = (requirement, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setFileErrors((prev) => ({
        ...prev,
        [requirement.Id]: "File exceeds the 8MB limit",
      }));
      return;
    }
    if (isDuplicateDocumentName(file.name, requirement.Id)) {
      setFileErrors((prev) => ({
        ...prev,
        [requirement.Id]: `"${file.name}" has already been uploaded for this asset. Please rename the file or choose a different one.`,
      }));
      return;
    }
    setStagedFiles((prev) => ({
      ...prev,
      [requirement.Id]: { file, documentName: requirement.DocumentName },
    }));
    setFileErrors((prev) => ({ ...prev, [requirement.Id]: undefined }));
    setDocActionErrors((prev) => ({ ...prev, [requirement.Id]: undefined }));
  };

  const handleDocumentRemove = async (requirement) => {
    const requirementId = requirement.Id;

    if (stagedFiles[requirementId]) {
      setStagedFiles((prev) => {
        const next = { ...prev };
        delete next[requirementId];
        return next;
      });
      return;
    }

    const existingDoc = getExistingDocument(requirementId);
    if (!existingDoc) return;

    const confirm = await swal({
      icon: "warning",
      title: "Delete document?",
      text: `This will permanently delete "${existingDoc.DocumentName}" from storage.`,
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      confirmButtonColor: "#d33",
    });
    if (!confirm.isConfirmed) return;

    setDeletingDocId(existingDoc.Id);
    try {
      await deleteFileFromStorage(existingDoc.ObjectName);
      await executeAnyMutation(
        "deleteCollateralDocument",
        { id: Number(existingDoc.Id), objectName: existingDoc.ObjectName },
        "collateralDocument",
        false,
        base_url,
      );
      setExistingDocuments((prev) =>
        prev.filter((d) => d.Id !== existingDoc.Id),
      );
    } catch {
      setDocActionErrors((prev) => ({
        ...prev,
        [requirementId]: "Could not remove this document. Try again.",
      }));
    } finally {
      setDeletingDocId(null);
    }
  };

  const isRequirementMandatory = (requirement, values) => {
    if (requirement.Requirement === "REQUIRED") return true;
    return false;
  };

  const getMissingRequiredDocs = (values) =>
    requirements.filter((r) => {
      if (!isRequirementMandatory(r, values)) return false;
      if (stagedFiles[r.Id]) return false;
      if (getExistingDocument(r.Id)) return false;
      return true;
    });

  const getPresignedUrl = async (objectName, expirySeconds = 3600) => {
    const response = await fetch(
      `${FILE_GATEWAY_URL}/filestorageservice/presigned-url?objectName=${encodeURIComponent(objectName)}&expiry=${expirySeconds}`,
      {
        method: "GET",
        headers: { "X-Api-Key": FILE_STORAGE_API_KEY },
      },
    );
    if (!response.ok) throw new Error("Failed to get download URL");
    const data = await response.json();
    return data.url;
  };

  const uploadStagedDocuments = async (collateralAssetId) => {
  const failures = []; // now: [{ requirementId, documentName, mandatory }]

  for (const [requirementId, staged] of Object.entries(stagedFiles)) {
    const requirement = requirements.find((r) => r.Id === Number(requirementId));
    const mandatory = requirement ? isRequirementMandatory(requirement, {}) : false;

    try {
      const existingDoc = getExistingDocument(Number(requirementId));
      if (existingDoc) {
        await deleteFileFromStorage(existingDoc.ObjectName).catch(() => { });
        await executeAnyMutation(
          "deleteCollateralDocument",
          { id: Number(existingDoc.Id), objectName: existingDoc.ObjectName },
          "collateralDocument", false, base_url,
        ).catch(() => { });
      }

      const minioResult = await uploadFileToStorage(staged.file, "maziwa", "collateral");

      const saveResult = await executeAnyMutation(
        "saveCollateralDocument",
        {
          collateralAssetId: Number(collateralAssetId),
          requirementId: Number(requirementId),
          documentName: staged.documentName,
          fileName: minioResult.fileName,
          objectName: minioResult.objectName,
          url: minioResult.url,
          mimeType: staged.file.type,
          fileSizeBytes: staged.file.size,
        },
        "collateralDocument",
        false,
        base_url,
      );

      if (saveResult?.error) {
        console.error("❌ DB save error:", saveResult.error);
        failures.push({ requirementId: Number(requirementId), documentName: staged.documentName, mandatory });
      }
    } catch (err) {
      console.error(`❌ Failed to upload "${staged.documentName}":`, err);
      failures.push({ requirementId: Number(requirementId), documentName: staged.documentName, mandatory });
    }
  }

  return failures;
};

  // Saves/updates ONLY the company owner record (name / reg number / KRA pin).
  // Directors are no longer touched here — they're managed in their own table
  // once the company owner exists.
  const saveOwnershipDetails = async (values, assetId) => {
    if (values.ownershipType !== "COMPANY_OWNED") return;

    const ownerPayload = {
      collateralAssetId: assetId,
      companyName: values.companyName,
      registrationNumber: values.companyRegNumber,
      kraPin: values.companyKraPin,
    };

    if (existingCompanyOwnerId) {
      const result = await executeAnyMutation(
        "updateCollateralCompanyOwner",
        { ...ownerPayload, id: Number(existingCompanyOwnerId) },
        "companyOwner",
        false,
        base_url,
      );
      if (result?.error)
        console.error("❌ Company owner update failed:", result.error);
    } else {
      const result = await executeAnyMutation(
        "saveCollateralCompanyOwner",
        ownerPayload,
        "companyOwner",
        false,
        base_url,
      );
      if (result?.error) {
        console.error("❌ Company owner save failed:", result.error);
      } else {
        try {
          const saved = JSON.parse(result.results);
          if (saved?.Id) setExistingCompanyOwnerId(saved.Id);
        } catch (e) {
          console.error("❌ Could not parse company owner result:", result, e);
        }
      }
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    if (activeStep !== 1) {
      setSubmitting(false);
      return;
    }

    const missing = getMissingRequiredDocs(values);
    if (missing.length > 0) {
      setActiveStep(1);
      swal({
        icon: "warning",
        title: "Missing required documents",
        html: `Please attach the following before saving:<br/><b>${missing.map((m) => m.DocumentName).join(", ")}</b>`,
      });
      setSubmitting(false);
      return;
    }

    try {
      const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
      const companyId = parseInt(decodedToken?.CompanyId);
      const customerId = parseInt(localStorage.getItem("CustomerId"));

      const submittedAssetType = assetTypes.find(
        (t) => t.Id === Number(values.assetTypeId),
      );
      const isVehicleAsset =
        submittedAssetType?.Code === VEHICLE_ASSET_TYPE_CODE;

      const metadataObject = {
        description: values.description || "",
        isThirdPartyAcquisition: !!values.isThirdPartyAcquisition,
        ltvPct:
          values.ltvPct !== "" && values.ltvPct != null
            ? parseFloat(values.ltvPct)
            : null,
      };
      if (isVehicleAsset) {
        Object.assign(metadataObject, {
          regNumber: values.regNumber || "",
          make: values.make || "",
          vehicleModel: values.vehicleModel || "",
          chassisNumber: values.chassisNumber || "",
          engineNumber: values.engineNumber || "",
          yearOfManufacture: values.yearOfManufacture || "",
          colour: values.colour || "",
        });
      }

      const metadata = JSON.stringify(metadataObject)
        .replace(/\\/g, "\\\\")
        .replace(/"/g, '\\"');

      const isAssetDataChanged = !props.isEditing || userChangedRef.current;
      const hasStagedFiles = Object.keys(stagedFiles).length > 0;

      if (props.isEditing && !isAssetDataChanged && !hasStagedFiles) {
        props.onClose();
        setSubmitting(false);
        swal({
          icon: "info",
          title: "No changes detected",
          text: "No asset fields were changed and no new documents were uploaded.",
          confirmButtonText: "OK",
        });
        return;
      }

      let assetId = props.data?.Id ?? null;

      // Editing with only new documents staged: skip the asset update and go
      // straight to the document upload, reusing the existing asset id.
      let response = isAssetDataChanged
        ? null
        : { results: JSON.stringify({ Id: props.data.Id }) };

      {
        const collateralAsset = {
          customerId,
          companyId,
          assetType: Number(values.assetTypeId),
          subType: Number(values.subTypeId),
          indicativeValue: parseFloat(values.indicativeValue),
          ltvPct:
            values.ltvPct !== "" && values.ltvPct != null
              ? parseFloat(values.ltvPct)
              : null,
          metadata,
          requireLien: !!values.requireLien,
          ownershipType: values.ownershipType,
        };
        if (props.isEditing) collateralAsset.id = props.data.Id;

        if (isAssetDataChanged) {
          response = await executeAnyMutation(
            props.isEditing ? "updateCollateralAsset" : "createCollateralAsset",
            collateralAsset,
            "collateralAsset",
            false,
            base_url,
          );
        }

        if (response?.results) {
          let savedAsset = null;
          try {
            savedAsset = JSON.parse(response.results);
          } catch {
            savedAsset = null;
          }
          assetId = savedAsset?.Id ?? props.data?.Id;

         const failedUploads = assetId
  ? await uploadStagedDocuments(assetId)
  : Object.keys(stagedFiles).map((reqId) => {
      const requirement = requirements.find((r) => r.Id === Number(reqId));
      return {
        requirementId: Number(reqId),
        documentName: stagedFiles[reqId].documentName,
        mandatory: requirement ? isRequirementMandatory(requirement, {}) : false,
      };
    });

const requiredFailures = failedUploads.filter((f) => f.mandatory);
const optionalFailures = failedUploads.filter((f) => !f.mandatory);
const wasNewAsset = !props.isEditing;

if (requiredFailures.length > 0) {
  // A mandatory document failed — this can't be treated as a successful save.
  if (wasNewAsset && assetId) {
    // Roll back the asset we just created so it doesn't sit half-finished.
    await executeAnyMutation(
      "deleteCollateralAsset",
      { id: Number(assetId) },
      "collateralAsset",
      false,
      base_url,
    ).catch((e) => console.error("❌ Rollback failed:", e));
  }

  props.onClose();
  props.onAction(); // refresh the list so a rolled-back row disappears

  swal({
    icon: "error",
    title: wasNewAsset ? "Asset not created" : "Update not completed",
    html: `The following mandatory document${requiredFailures.length > 1 ? "s" : ""} failed to upload:<br/><b>${requiredFailures.map((f) => f.documentName).join(", ")}</b><br/><br/>${wasNewAsset ? "The asset was not saved." : "Please retry saving once the issue is resolved."} Please try again.`,
  });
  setSubmitting(false);
  return;
}

// Company owner save/update only — co-owner and directors live elsewhere now.
await saveOwnershipDetails(values, assetId);

props.onClose();
props.onAction();

const nextStepMessage =
  values.ownershipType === "CO_OWNED"
    ? `<br/><br/>Next: use the <b>"Manage Co-Owner"</b> action on this asset to add the co-owner's details.`
    : values.ownershipType === "COMPANY_OWNED"
      ? `<br/><br/>Next: use the <b>"Manage Directors"</b> action on this asset to add at least 2 directors.`
      : "";

if (optionalFailures.length > 0) {
  swal({
    icon: "warning",
    title: `Asset ${props.isEditing ? "updated" : "registered"}`,
    html: `Saved successfully, but these optional documents failed to upload — re-attach from the edit screen:<br/><b>${optionalFailures.map((f) => f.documentName).join(", ")}</b>${nextStepMessage}`,
  });
} else {
  swal({
    icon: "success",
    title: "Success!",
    html: `Collateral asset ${props.isEditing ? "updated" : "registered"} successfully.${wasNewAsset ? nextStepMessage : ""}`,
  });
}

         
        } else if (response?.error) {
          let errorMessage = "An unknown error occurred.";
          try {
            const errorObj =
              typeof response.error === "string"
                ? JSON.parse(response.error)
                : response.error;
            if (typeof errorObj === "string") {
              errorMessage = errorObj;
            } else if (errorObj?.error) {
              errorMessage = errorObj.error;
            } else if (errorObj?.Error) {
              errorMessage =
                errorObj.Error.match(/System\.Exception: (.*?)\./)?.[1] ||
                errorObj.Error;
            }
          } catch {
            errorMessage = response.error;
          }
          swal({ icon: "error", title: "Error!", text: errorMessage });
        }
      }
    } catch (error) {
      swal({
        icon: "error",
        title: "Error!",
        text: "An error occurred while saving the collateral asset. Please try again.",
      });
    }
    setSubmitting(false);
  };

  const cardSx = {
    borderRadius: "10px",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e4eaf3"}`,
    bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#fff",
  };

  return (
    <Box m="20px">
      <Header
        title={
          props.isEditing
            ? "Edit Collateral Asset"
            : "Register Collateral Asset"
        }
        subtitle={
          props.isEditing
            ? "Update this asset's details"
            : "Capture asset details and its supporting documents"
        }
      />

      {props.isEditing &&
        props.data?.Status === "REJECTED" &&
        props.data?.RejectionReason && (
          <Alert severity="error" sx={{ mb: 2 }}>
            This asset was rejected: {props.data.RejectionReason}
          </Alert>
        )}

      <Formik
        enableReinitialize
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          isSubmitting,
          validateForm,
        }) => {
          setFieldValueRef.current = setFieldValue;
          const currentSubType = subTypes.find(
            (s) => s.Id === Number(values.subTypeId),
          );
          const currentAssetType = assetTypes.find(
            (t) => t.Id === Number(values.assetTypeId),
          );
          const isVehicleAsset =
            currentAssetType?.Code === VEHICLE_ASSET_TYPE_CODE;
          const missingRequiredDocs = getMissingRequiredDocs(values);
          const requiredDocsTotal = requirements.filter((r) =>
            isRequirementMandatory(r, values),
          ).length;
          const requiredDocsAttached =
            requiredDocsTotal - missingRequiredDocs.length;

          const requirementsByTier = {
            REQUIRED: requirements.filter((r) => r.Requirement === "REQUIRED"),
            CONDITIONAL: requirements.filter(
              (r) => r.Requirement === "CONDITIONAL",
            ),
            SUPPORTING: requirements.filter(
              (r) => r.Requirement === "SUPPORTING",
            ),
          };

          const goToDocumentsStep = async (e) => {
            e?.preventDefault();
            e?.stopPropagation();
            const validationErrors = await validateForm();
            const step1Fields = [
              "assetTypeId",
              "subTypeId",
              "indicativeValue",
              "ltvPct",
              ...(isVehicleAsset ? VEHICLE_REQUIRED_FIELDS : []),
            ];
            const hasErrors = step1Fields.some(
              (name) => validationErrors[name],
            );
            if (hasErrors) {
              step1Fields.forEach((name) => setFieldTouched(name, true, true));
              const firstErrorField = step1Fields.find(
                (name) => validationErrors[name],
              );
              if (firstErrorField)
                document.querySelector(`[name="${firstErrorField}"]`)?.focus();
              return;
            }
            setActiveStep(1);
          };

          const renderRequirementCard = (requirement) => {
            const mandatory = isRequirementMandatory(requirement, values);
            const staged = stagedFiles[requirement.Id];
            const existingDoc = getExistingDocument(requirement.Id);
            const isDeleting = deletingDocId === existingDoc?.Id;
            const displayName = staged?.file?.name || existingDoc?.FileName;
            const tierMeta =
              TIER_META[requirement.Requirement] || TIER_META.SUPPORTING;

            const handleViewDocument = async () => {
              if (!existingDoc?.ObjectName) return;
              try {
                const url = await getPresignedUrl(existingDoc.ObjectName);
                window.open(url, "_blank");
              } catch {
                swal({
                  icon: "error",
                  title: "Error",
                  text: "Could not generate download link. Please try again.",
                });
              }
            };

            return (
              <Card
                key={requirement.Id}
                variant="outlined"
                sx={{ ...cardSx, mb: 1.5 }}
              >
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Box
                    display="flex"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    gap={1}
                  >
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      <DescriptionOutlinedIcon
                        fontSize="small"
                        sx={{
                          mt: "2px",
                          color: isDark ? "rgba(255,255,255,0.5)" : "#64748b",
                        }}
                      />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {requirement.DocumentName}
                        </Typography>
                        <Box display="flex" gap={0.5} mt={0.5} flexWrap="wrap">
                          <Chip
                            label={mandatory ? "Required" : tierMeta.label}
                            size="small"
                            color={mandatory ? "error" : tierMeta.color}
                            sx={{
                              height: 20,
                              fontSize: "0.65rem",
                              fontWeight: 700,
                            }}
                          />
                          {requirement.Requirement === "CONDITIONAL" &&
                            !mandatory && (
                              <Tooltip
                                title={
                                  requirement.RequiredAboveAmount
                                    ? `Becomes required if value exceeds KES ${Number(requirement.RequiredAboveAmount).toLocaleString()}`
                                    : requirement.RequiredIfThirdParty
                                      ? "Required for third-party acquisitions"
                                      : "Conditionally required"
                                }
                              >
                                <InfoOutlinedIcon
                                  fontSize="small"
                                  sx={{
                                    color: isDark
                                      ? "rgba(255,255,255,0.4)"
                                      : "#94a3b8",
                                    cursor: "help",
                                  }}
                                />
                              </Tooltip>
                            )}
                        </Box>
                      </Box>
                    </Box>

                    {displayName ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          icon={
                            <CheckCircleIcon
                              sx={{ fontSize: "16px !important" }}
                            />
                          }
                          label={displayName}
                          size="small"
                          color={staged ? "info" : "success"}
                          variant="outlined"
                          sx={{
                            maxWidth: 200,
                            cursor: existingDoc ? "pointer" : "default",
                          }}
                          onClick={
                            !staged && existingDoc
                              ? handleViewDocument
                              : undefined
                          }
                          clickable={!staged && !!existingDoc}
                        />
                        <Tooltip
                          title={staged ? "Remove" : "Delete saved document"}
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={isDeleting}
                              onClick={() => handleDocumentRemove(requirement)}
                            >
                              {isDeleting ? (
                                <CircularProgress size={16} />
                              ) : (
                                <DeleteOutlineIcon
                                  fontSize="small"
                                  color={staged ? "inherit" : "error"}
                                />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Button
                        component="label"
                        size="small"
                        variant={mandatory ? "contained" : "outlined"}
                        color={mandatory ? "error" : "primary"}
                        startIcon={<UploadFileOutlinedIcon />}
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        Upload
                        <input
                          type="file"
                          hidden
                          accept="image/*,.pdf"
                          onChange={(e) =>
                            handleDocumentSelect(requirement, e.target.files[0])
                          }
                        />
                      </Button>
                    )}
                  </Box>

                  {staged && (
                    <Typography
                      variant="caption"
                      color="info.main"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      ⏳ Will be uploaded when you save the asset
                    </Typography>
                  )}
                  {fileErrors[requirement.Id] && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {fileErrors[requirement.Id]}
                    </Typography>
                  )}
                  {docActionErrors[requirement.Id] && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {docActionErrors[requirement.Id]}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            );
          };

          return (
            <form onSubmit={handleSubmit}>
              <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                <Step>
                  <StepLabel>Asset Details</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Supporting Documents</StepLabel>
                </Step>
              </Stepper>

              {activeStep === 0 && (
                <Box
                  display="grid"
                  gap="20px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                >
                  <TextField
                    select
                    fullWidth
                    variant="filled"
                    label="Asset Type *"
                    name="assetTypeId"
                    value={
                      values.assetTypeId !== "" &&
                      values.assetTypeId != null &&
                      assetTypes.some(
                        (t) => Number(t.Id) === Number(values.assetTypeId),
                      )
                        ? Number(values.assetTypeId)
                        : ""
                    }
                    onChange={(e) => {
                      const value =
                        e.target.value !== "" ? Number(e.target.value) : "";
                      userChangedRef.current = true;
                      setFieldValue("assetTypeId", value);
                      setFieldValue("subTypeId", "");
                      setStagedFiles({});
                      loadDependents(value);
                      const selectedType = assetTypes.find(
                        (t) => Number(t.Id) === Number(value),
                      );
                      setFieldValue(
                        "isVehicleAsset",
                        selectedType?.Code === VEHICLE_ASSET_TYPE_CODE,
                      );
                    }}
                    disabled={loadingTypes}
                    onBlur={handleBlur}
                    error={!!touched.assetTypeId && !!errors.assetTypeId}
                    helperText={
                      (touched.assetTypeId && errors.assetTypeId) ||
                      (loadingTypes ? "Loading asset types…" : " ")
                    }
                    sx={{ gridColumn: "span 2" }}
                  >
                    {assetTypes.map((t) => (
                      <MenuItem key={t.Id} value={Number(t.Id)}>
                        {t.DisplayName || t.Name || t.AssetTypeName || t.Code}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    fullWidth
                    variant="filled"
                    label="Asset Sub-Type *"
                    name="subTypeId"
                    value={
                      values.subTypeId !== "" &&
                      values.subTypeId != null &&
                      subTypes.some(
                        (s) => Number(s.Id) === Number(values.subTypeId),
                      )
                        ? Number(values.subTypeId)
                        : ""
                    }
                    onChange={(e) => {
                      userChangedRef.current = true;
                      const selectedVal =
                        e.target.value !== "" ? Number(e.target.value) : "";
                      setFieldValue("subTypeId", selectedVal);
                      const selectedSub = subTypes.find(
                        (s) => Number(s.Id) === Number(selectedVal),
                      );
                      const subTypeName =
                        selectedSub?.DisplayName ||
                        selectedSub?.Name ||
                        selectedSub?.SubTypeName ||
                        selectedSub?.Code;

                      // Reset model & make if existing model doesn't match the new subtype
                      if (values.vehicleModel) {
                        const currentModelObj = carModels.find(
                          (m) => m.ModelName === values.vehicleModel,
                        );
                        if (
                          currentModelObj &&
                          !matchBodyTypeToSubType(
                            currentModelObj.BodyType || currentModelObj.bodyType,
                            subTypeName,
                          )
                        ) {
                          setFieldValue("vehicleModel", "");
                          setFieldValue("make", "");
                        }
                      }

                      if (!values.vehicleModel) {
                        const defaultLtv =
                          selectedSub?.DefaultMaxLtvPct ??
                          selectedSub?.defaultMaxLtvPct ??
                          "";
                        setFieldValue("ltvPct", defaultLtv);
                      }
                    }}
                    onBlur={handleBlur}
                    disabled={!values.assetTypeId || loadingDependents}
                    error={!!touched.subTypeId && !!errors.subTypeId}
                    helperText={
                      (touched.subTypeId && errors.subTypeId) ||
                      (loadingDependents ? "Loading sub-types…" : " ")
                    }
                    sx={{ gridColumn: "span 2" }}
                  >
                    {subTypes.map((s) => (
                      <MenuItem key={s.Id} value={Number(s.Id)}>
                        {s.DisplayName || s.Name || s.SubTypeName || s.Code}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    fullWidth
                    variant="filled"
                    label="Asset Ownership *"
                    name="ownershipType"
                    value={values.ownershipType}
                    onChange={(e) => {
                      userChangedRef.current = true;
                      setFieldValue("ownershipType", e.target.value);
                      loadDependents(values.assetTypeId, e.target.value);
                    }}
                    sx={{ gridColumn: "span 4" }}
                  >
                    {OWNERSHIP_TYPES.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  {values.ownershipType === "CO_OWNED" && (
                    <Box sx={{ gridColumn: "span 4" }}>
                      <Alert severity="info">
                        Co-owner details are captured separately. Once this
                        asset is saved, use the <b>"Manage Co-Owner"</b> action
                        on the assets table to add the co-owner's information.
                      </Alert>
                    </Box>
                  )}

                  {values.ownershipType === "COMPANY_OWNED" && (
                    <Box sx={{ gridColumn: "span 4" }}>
                      <Divider sx={{ mb: 2 }} />
                      <Typography variant="subtitle2" fontWeight={700} mb={2}>
                        Company Owner Details
                      </Typography>
                      <Box
                        display="grid"
                        gap="20px"
                        gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                      >
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Company Name *"
                          name="companyName"
                          value={values.companyName}
                          onChange={(e) => {
                            userChangedRef.current = true;
                            handleChange(e);
                          }}
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Registration Number (CR12) *"
                          name="companyRegNumber"
                          value={values.companyRegNumber}
                          onChange={(e) => {
                            userChangedRef.current = true;
                            handleChange(e);
                          }}
                          sx={{ gridColumn: "span 1" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="KRA PIN"
                          name="companyKraPin"
                          value={values.companyKraPin}
                          onChange={(e) => {
                            userChangedRef.current = true;
                            handleChange(e);
                          }}
                          sx={{ gridColumn: "span 1" }}
                        />
                      </Box>
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Directors are captured separately. Once this asset is
                        saved, use the <b>"Manage Directors"</b> action on the
                        assets table to add at least 2 directors.
                      </Alert>
                    </Box>
                  )}

                  <TextField
                    fullWidth
                    variant="filled"
                    label="Indicative Value (KES) *"
                    name="indicativeValue"
                    value={
                      values.indicativeValue !== "" &&
                      values.indicativeValue != null
                        ? String(values.indicativeValue)
                            .replace(/,/g, "")
                            .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                        : ""
                    }
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/,/g, "");
                      if (/^\d*\.?\d*$/.test(rawValue)) {
                        userChangedRef.current = true;
                        setFieldValue("indicativeValue", rawValue);
                      }
                    }}
                    onBlur={handleBlur}
                    error={
                      !!touched.indicativeValue && !!errors.indicativeValue
                    }
                    helperText={
                      touched.indicativeValue && errors.indicativeValue
                    }
                    sx={{ gridColumn: "span 2" }}
                  />

                  <Box
                    sx={{
                      gridColumn: "span 2",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.isThirdPartyAcquisition}
                          onChange={(e) => {
                            userChangedRef.current = true;
                            setFieldValue(
                              "isThirdPartyAcquisition",
                              e.target.checked,
                            );
                          }}
                        />
                      }
                      label="Asset is being acquired from a third party (dealer/seller)"
                    />
                  </Box>

                  <Box
                    sx={{
                      gridColumn: "span 2",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.requireLien}
                          onChange={(e) => {
                            userChangedRef.current = true;
                            setFieldValue("requireLien", e.target.checked);
                          }}
                        />
                      }
                      label="Register a lien against this asset"
                    />
                  </Box>

                  {isVehicleAsset && (
                    <Box sx={{ gridColumn: "span 4" }}>
                      <Divider sx={{ mb: 2 }} />
                      <Typography variant="subtitle2" fontWeight={700} mb={2}>
                        Vehicle Details
                      </Typography>
                      <Box
                        display="grid"
                        gap="20px"
                        gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                      >
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Number Plate *"
                          name="regNumber"
                          value={values.regNumber}
                          onChange={(e) => {
                            userChangedRef.current = true;
                            handleChange(e);
                          }}
                          onBlur={handleBlur}
                          error={!!touched.regNumber && !!errors.regNumber}
                          helperText={
                            (touched.regNumber && errors.regNumber) || " "
                          }
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          select
                          variant="filled"
                          label="Make *"
                          name="make"
                          value={values.make}
                          onChange={(e) => {
                            userChangedRef.current = true;
                            const selectedName = e.target.value;
                            setFieldValue("make", selectedName);
                            setFieldValue("vehicleModel", "");
                            const selectedSub = subTypes.find(
                              (s) => Number(s.Id) === Number(values.subTypeId),
                            );
                            const defaultLtv =
                              selectedSub?.DefaultMaxLtvPct ??
                              selectedSub?.defaultMaxLtvPct ??
                              "";
                            setFieldValue("ltvPct", defaultLtv);
                          }}
                          onBlur={handleBlur}
                          error={!!touched.make && !!errors.make}
                          helperText={(touched.make && errors.make) || " "}
                          disabled={loadingCarModels}
                          sx={{ gridColumn: "span 2" }}
                        >
                          <MenuItem value="">
                            {loadingCarModels
                              ? "Loading makes..."
                              : "-- Select Make --"}
                          </MenuItem>
                          {(() => {
                            const selectedSub = subTypes.find(
                              (s) => Number(s.Id) === Number(values.subTypeId),
                            );
                            const subTypeName =
                              selectedSub?.DisplayName ||
                              selectedSub?.Name ||
                              selectedSub?.SubTypeName ||
                              selectedSub?.Code;

                            // Filter models matching this subtype's body type (e.g. SUV, Saloon, etc.)
                            const matchingModelsForSubType = carModels.filter((m) =>
                              matchBodyTypeToSubType(m.BodyType || m.bodyType, subTypeName)
                            );

                            let availableMakesList = [];
                            if (matchingModelsForSubType.length > 0) {
                              const validMakeIds = new Set(
                                matchingModelsForSubType
                                  .map((m) => Number(m.MakeId))
                                  .filter(Boolean)
                              );
                              const validMakeNames = new Set(
                                matchingModelsForSubType
                                  .map((m) => (m.Make || "").toLowerCase())
                                  .filter(Boolean)
                              );

                              availableMakesList = [
                                ...new Set([
                                  ...carMakesList
                                    .filter(
                                      (m) =>
                                        validMakeIds.has(Number(m.Id || m.id)) ||
                                        validMakeNames.has(
                                          (m.MakeName || m.Name || m.Make || "").toLowerCase()
                                        )
                                    )
                                    .map((m) => m.MakeName || m.Name || m.Make),
                                  ...matchingModelsForSubType.map((m) => m.Make),
                                ]),
                              ].filter(Boolean);
                            } else {
                              availableMakesList = [
                                ...new Set([
                                  ...carMakesList.map(
                                    (m) => m.MakeName || m.Name || m.Make
                                  ),
                                  ...carModels.map((m) => m.Make),
                                ]),
                              ].filter(Boolean);
                            }

                            return availableMakesList.sort().map((makeName) => {
                              const makeObj = carMakesList.find(
                                (m) =>
                                  (m.MakeName || m.Name || m.Make) === makeName
                              );
                              return (
                                <MenuItem
                                  key={makeObj?.Id || makeName}
                                  value={makeName}
                                >
                                  {makeName}{" "}
                                  {makeObj?.Code ? `(${makeObj.Code})` : ""}
                                </MenuItem>
                              );
                            });
                          })()}
                        </TextField>
                        <TextField
                          fullWidth
                          select
                          variant="filled"
                          label="Model *"
                          name="vehicleModel"
                          value={values.vehicleModel}
                          onChange={(e) => {
                            userChangedRef.current = true;
                            const modelName = e.target.value;
                            setFieldValue("vehicleModel", modelName);

                            const selectedMakeObj = carMakesList.find(
                              (m) =>
                                (m.MakeName || m.Name || m.Make) ===
                                values.make,
                            );
                            const selectedMakeId =
                              selectedMakeObj?.Id || selectedMakeObj?.id;
                            const matchedModel = carModels.find((m) => {
                              const nameMatch = m.ModelName === modelName;
                              if (!nameMatch) return false;
                              if (selectedMakeId && m.MakeId) {
                                return (
                                  Number(m.MakeId) === Number(selectedMakeId)
                                );
                              }
                              return (
                                m.Make === values.make ||
                                (m.Make &&
                                  values.make &&
                                  m.Make.toLowerCase() ===
                                    values.make.toLowerCase())
                              );
                            });

                            const selectedSub = subTypes.find(
                              (s) => Number(s.Id) === Number(values.subTypeId),
                            );
                            const modelLtv =
                              matchedModel?.LtvPct ??
                              matchedModel?.ltvPct ??
                              selectedSub?.DefaultMaxLtvPct ??
                              selectedSub?.defaultMaxLtvPct ??
                              "";
                            setFieldValue(
                              "ltvPct",
                              modelLtv !== null && modelLtv !== undefined
                                ? modelLtv
                                : "",
                            );
                          }}
                          onBlur={handleBlur}
                          error={
                            !!touched.vehicleModel && !!errors.vehicleModel
                          }
                          helperText={
                            (touched.vehicleModel && errors.vehicleModel) || " "
                          }
                          disabled={!values.make || loadingCarModels}
                          sx={{ gridColumn: "span 2" }}
                        >
                          <MenuItem value="">
                            {!values.make
                              ? "Select a make first"
                              : "-- Select Model --"}
                          </MenuItem>
                          {(() => {
                            const selectedMakeObj = carMakesList.find(
                              (m) =>
                                (m.MakeName || m.Name || m.Make) ===
                                values.make,
                            );
                            const selectedMakeId =
                              selectedMakeObj?.Id || selectedMakeObj?.id;

                            const selectedSub = subTypes.find(
                              (s) => Number(s.Id) === Number(values.subTypeId),
                            );
                            const subTypeName =
                              selectedSub?.DisplayName ||
                              selectedSub?.Name ||
                              selectedSub?.SubTypeName ||
                              selectedSub?.Code;

                            // 1. Filter models by the selected make
                            const modelsByMake = carModels.filter((m) => {
                              if (selectedMakeId && m.MakeId) {
                                return (
                                  Number(m.MakeId) === Number(selectedMakeId)
                                );
                              }
                              return (
                                m.Make === values.make ||
                                (m.Make &&
                                  values.make &&
                                  m.Make.toLowerCase() ===
                                    values.make.toLowerCase())
                              );
                            });

                            // 2. Filter models matching the selected subtype's body type (e.g. SUV)
                            const modelsForSubType = modelsByMake.filter((m) =>
                              matchBodyTypeToSubType(
                                m.BodyType || m.bodyType,
                                subTypeName,
                              )
                            );

                            const finalModels =
                              modelsForSubType.length > 0
                                ? modelsForSubType
                                : modelsByMake;

                            return finalModels.map((m) => (
                              <MenuItem key={m.Id} value={m.ModelName}>
                                {m.ModelName}{" "}
                                {m.BodyType ? `[${m.BodyType}]` : ""}
                              </MenuItem>
                            ));
                          })()}
                        </TextField>
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Chassis / VIN Number *"
                          name="chassisNumber"
                          value={values.chassisNumber}
                          onChange={(e) => {
                            userChangedRef.current = true;
                            handleChange(e);
                          }}
                          onBlur={handleBlur}
                          error={
                            !!touched.chassisNumber && !!errors.chassisNumber
                          }
                          helperText={
                            (touched.chassisNumber && errors.chassisNumber) ||
                            " "
                          }
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Engine Number *"
                          name="engineNumber"
                          value={values.engineNumber}
                          onChange={(e) => {
                            userChangedRef.current = true;
                            handleChange(e);
                          }}
                          onBlur={handleBlur}
                          error={
                            !!touched.engineNumber && !!errors.engineNumber
                          }
                          helperText={
                            (touched.engineNumber && errors.engineNumber) || " "
                          }
                          sx={{ gridColumn: "span 2" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          type="number"
                          label="Year of Manufacture *"
                          name="yearOfManufacture"
                          value={values.yearOfManufacture}
                          onChange={(e) => {
                            userChangedRef.current = true;
                            handleChange(e);
                          }}
                          onBlur={handleBlur}
                          error={
                            !!touched.yearOfManufacture &&
                            !!errors.yearOfManufacture
                          }
                          helperText={
                            (touched.yearOfManufacture &&
                              errors.yearOfManufacture) ||
                            " "
                          }
                          sx={{ gridColumn: "span 1" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          label="Colour *"
                          name="colour"
                          value={values.colour}
                          onChange={(e) => {
                            userChangedRef.current = true;
                            handleChange(e);
                          }}
                          onBlur={handleBlur}
                          error={!!touched.colour && !!errors.colour}
                          helperText={(touched.colour && errors.colour) || " "}
                          sx={{ gridColumn: "span 1" }}
                        />
                        <TextField
                          fullWidth
                          variant="filled"
                          type="number"
                          label="LTV (%)"
                          name="ltvPct"
                          InputProps={{ readOnly: true }}
                          disabled
                          value={values.ltvPct ?? ""}
                          onBlur={handleBlur}
                          error={!!touched.ltvPct && !!errors.ltvPct}
                          helperText={
                            (touched.ltvPct && errors.ltvPct) || "Max 100%"
                          }
                          sx={{ gridColumn: "span 2" }}
                        />
                      </Box>
                    </Box>
                  )}

                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    variant="filled"
                    label="Additional Details (Optional)"
                    name="description"
                    value={values.description}
                    onChange={(e) => {
                      userChangedRef.current = true;
                      handleChange(e);
                    }}
                    onBlur={handleBlur}
                    sx={{ gridColumn: "span 4" }}
                  />

                  {currentSubType && (
                    <Box sx={{ gridColumn: "span 4" }}>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        {currentSubType.RequiresSpousalConsent && (
                          <Chip
                            size="small"
                            color="warning"
                            label="Requires spousal consent"
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {activeStep === 1 && (
                <Box>
                  {loadingDocuments ? (
                    <Box display="flex" justifyContent="center" py={3}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : requirements.length === 0 ? (
                    <Alert severity="info">
                      No document requirements configured for this asset type
                      yet.
                    </Alert>
                  ) : (
                    <>
                      <Alert
                        severity={
                          missingRequiredDocs.length > 0 ? "warning" : "success"
                        }
                        sx={{ mb: 2 }}
                      >
                        {missingRequiredDocs.length > 0
                          ? `${requiredDocsAttached}/${requiredDocsTotal} mandatory documents attached. Missing: ${missingRequiredDocs.map((m) => m.DocumentName).join(", ")}`
                          : `All ${requiredDocsTotal} mandatory documents attached.`}
                      </Alert>

                      {requirementsByTier.REQUIRED.length > 0 && (
                        <>
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            mb={1}
                          >
                            Mandatory Documents
                          </Typography>
                          {requirementsByTier.REQUIRED.map(
                            renderRequirementCard,
                          )}
                        </>
                      )}
                      {requirementsByTier.CONDITIONAL.length > 0 && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            mb={1}
                          >
                            If Applicable Documents
                          </Typography>
                          {requirementsByTier.CONDITIONAL.map(
                            renderRequirementCard,
                          )}
                        </>
                      )}
                      {requirementsByTier.SUPPORTING.length > 0 && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            mb={1}
                          >
                            Optional Documents
                          </Typography>
                          {requirementsByTier.SUPPORTING.map(
                            renderRequirementCard,
                          )}
                        </>
                      )}
                    </>
                  )}
                </Box>
              )}

              <Box display="flex" justifyContent="space-between" mt="24px">
                <Button
                  type="button"
                  variant="outlined"
                  disabled={activeStep === 0}
                  onClick={() => setActiveStep(0)}
                >
                  Back
                </Button>
                {activeStep === 0 ? (
                  <Button
                    type="button"
                    color="secondary"
                    variant="contained"
                    onClick={(e) => goToDocumentsStep(e)}
                  >
                    Next: Documents
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    color="secondary"
                    variant="contained"
                    disabled={isSubmitting || missingRequiredDocs.length > 0}
                  >
                    {isSubmitting
                      ? "Saving…"
                      : props.isEditing
                        ? "Update Asset"
                        : "Save Collateral Asset"}
                  </Button>
                )}
              </Box>
              {isSubmitting && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default CollateralAssetForm;
